import {Dispatch, FC, useEffect, useState} from 'react';
import Modal from 'react-modal';
import {Button} from '../Button';
import Error from '../Error';
import {tenantPopupData} from '../../../constants/listData';
import Spinner from '../Spinner';
import {sendGetRequest} from '../../../api/requests';
import {TenantProps} from '../../../constants/types';
import {Link} from 'react-router-dom';
import {listPopupStyles} from '../../../constants/popupStyles';
import {useAppSelector} from '../../../store/hooks';
import permissionsInfo from '../../../constants/permissionsInfo';
import {Checkbox} from '@mui/material';

Modal.setAppElement(document.getElementById('root'));

interface ConfirmationPopupProps {
  isOpen: boolean;
  setIsOpen: Dispatch<boolean>;
  confirmableAction: any;
  entityName: string;
  entityKey: string;
  entitiesToAdd: number[];
  setEntitiesToAdd: Dispatch<number[]>;
  entitiesList?: TenantProps[],
  customRoute?: string;
}

const ListPopup: FC<ConfirmationPopupProps> = ({
  isOpen,
  setIsOpen,
  confirmableAction,
  entityName,
  entityKey,
  entitiesToAdd,
  setEntitiesToAdd,
  entitiesList,
  customRoute
}) => {
  const query = {
    direction: 'asc',
    page: '1',
    per_page: '1000000',
    sort: 'SLUG'
  };
  const entity = tenantPopupData[entityKey];
  const tableData = entity?.['fields'];
  const hasSorting = entity.hasSorting || false;

  const {permissions: currentUserPermissions} = useAppSelector((state) =>
    state.permissions);

  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [sortDirection, setSortDirection] = useState(query.direction || 'asc');
  const [sortedBy, setSortedBy] = useState(query.sort || 'SLUG');

  useEffect(() => {
    (async () => {
      if (hasSorting && !!data) return;

      try {
        const response = await sendGetRequest(
          customRoute ? customRoute : entity.fetchUrl(),
          {...query, direction: sortDirection, sort: sortedBy}
        );
        let content = response.content || response;

        /* TODO: Switch to specific API endpoint that filters the entries by
            itself, when it will be available.*/
        content = content.filter((i) =>
          !entitiesList?.filter((j) => j.id === i.id).length);
        setData(content);
      } catch (error) {
        if (error instanceof ErrorEvent) {
          setError(error?.message || '');
        }
      }
    })();
    return () => {
      setError('');
    };
  }, [sortDirection, sortedBy]);

  const closeModal = () =>{
    setIsOpen(false);
  };

  const sortTableItems = (sortName: string) => {
    if (hasSorting) {
      const sortContent = data.sort((item, prevItem) =>
        item[sortName.toLowerCase()].localeCompare(
            prevItem[sortName.toLowerCase()]
        )
      );

      sortDirection === 'desc' ? setData(sortContent.reverse()) : setData(sortContent);
    }

    if (sortName === sortedBy) {
      const currentSortDirection = sortDirection === 'desc' ? 'asc' : 'desc';
      setSortDirection(currentSortDirection);
    } else {
      setSortedBy(sortName);
      setSortDirection('asc');
    }
  };

  const handleCheckboxChange = (id, event) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      setEntitiesToAdd([...entitiesToAdd, Number(id)]);
    } else {
      setEntitiesToAdd(entitiesToAdd.filter((value) => value !== Number(id)));
    }
  };

  const handleHeadCheckboxChange = (event) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      setEntitiesToAdd(data.map(({id}) => id));
    } else {
      setEntitiesToAdd([]);
    }
  };

  const getBtnLabel = () => {
    if (entity.addBtnLabel.length === 1) {
      return entity.addBtnLabel[0];
    } else {
      return entitiesToAdd.length === 1 ?
      `Add ${entitiesToAdd.length} ${entity.addBtnLabel[0]}` :
      `Add ${entitiesToAdd.length} ${entity.addBtnLabel[1]}`;
    }
  };

  const renderRows = () => data
      .map((rowItem, rowIndex: number) => {
        const rowCells = tableData.map(({name, link}) => {
          const label = rowItem[name];
          const isSysAdmin =
              permissionsInfo.users.manage.groups.some((permission) =>
                currentUserPermissions.includes(permission));
          return (
            <td
              key={`${name}-row-${rowIndex}`}
              className="cell">
              {!!link && isSysAdmin ?
                (<Link
                  to={link(rowItem?.id)}
                  className="link">{label}
                </Link>) : label}
            </td>);
        });

        return (
          <tr className="row" key={rowItem.id}>
            <td className="checkbox-cell cell">
              <Checkbox
                size="small"
                onChange={(event) => handleCheckboxChange(rowItem.id, event)}
                checked={entitiesToAdd.includes(rowItem.id)}
              />
            </td>
            {rowCells}
          </tr>
        );
      });

  const renderNames = ({name, label, sortName}) => {
    const className = sortName === sortedBy ?
      `${sortDirection}-icon pointer` : 'pointer';

    return (
      <th
        key={name}
        className="cell header-cell">
        <span
          className={className}
          onClick={() => sortTableItems(sortName)}
        >
          {label}
        </span>
      </th>);
  };

  const tableContent = !data ? <Spinner loadingText={entity?.loadingText}/> :
    !data?.length?
      <p className="warning">
        {`${entity?.entityType} "${entityName}"` +
        ` has been already added to all tenants.`}
      </p> :
    <table className="popup-table">
      <thead>
        <tr className="header-row">
          {data.length ? <th className="checkbox-cell cell" id="head-checkbox">
            <Checkbox
              size="small"
              onChange={(event) => handleHeadCheckboxChange(event)}
              checked={entitiesToAdd.length === data.length}
            />
          </th> : null}
          {tableData.map(renderNames)}
        </tr>
      </thead>
      <tbody>
        {renderRows()}
      </tbody>
    </table>;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      style={listPopupStyles}
      contentLabel="List Popup"
    >
      <div className="list-popup">
        <h4 className="popup-title">{entity.title}</h4>
        <div className="popup-table-container">
          {error ? <Error message={error} /> : tableContent}
        </div>
        <div className="buttons">
          <Button label={entity.closeBtnLabel}
            variant="outlined"
            color="secondary"
            onClick={closeModal}
          />
          {(data && entitiesToAdd.length) || customRoute?
            <Button
              label={getBtnLabel()}
              variant="contained"
              color="success"
              onClick={confirmableAction}/> : null}
        </div>
      </div>
    </Modal>
  );
};

export default ListPopup;
